import { MODE } from '@env';
import { Trans } from '@lingui/react/macro';
import { IS_LOCAL_DEV } from '@utils/constants';
import { jsonStringify } from '@utils/json';
import { reportCustomError } from '@utils/reportCustomError';
import { last } from 'lodash-es';
import { ComponentType, FC, Suspense, lazy, memo } from 'react';
import {
  IndexRouteProps,
  Navigate,
  PathRouteProps,
  Route as RouteV6,
  Routes,
} from 'react-router-dom';

export type LoadReturn = Promise<anyOk>;

type PathImport = () => Promise<anyOk>;

const NoImport: FC = () => {
  if (MODE === 'production') {
    return (
      <div>
        <Trans>
          Something went wrong trying to load this page. You should report this
          to the Mastery team.
        </Trans>
      </div>
    );
  }
  return (
    <div data-testid="export-not-found">
      <Trans>
        Export not found. You tried to lazy-load a route but no exports from the
        file could be found. Lazy will either load an export called{' '}
        <code>View</code>
        or the last export it finds.
      </Trans>
    </div>
  );
};

interface ImportFile extends Record<string, ComponentType | undefined> {
  View?: ComponentType;
}

const createLazyComponent = (
  loadImportFn: () => Promise<ImportFile>,
): ComponentType =>
  lazy(() =>
    loadImportFn()
      .then((comp) => {
        // we assume that each view has an export called "View"
        // we fallback to the last export we find
        // if there is more than one export, routes might not load correctly ymmv
        return {
          default: comp.View || last(Object.values(comp)) || NoImport,
        };
      })
      .catch((err) => {
        reportCustomError(err);
        if (IS_LOCAL_DEV) {
          /* eslint-disable no-console */
          console.error(err);
        }
        return { default: NoImport };
      }),
  );

export type LazyRoutesArrObj = (PathRouteProps | IndexRouteProps) & {
  load: PathImport;
};

/** Route paths are tracked in app/routes. You must add them there for TS to pass. */
export type LazyRoutesArr = LazyRoutesArrObj[];

interface Props {
  routes: LazyRoutesArr;
  /** If a route is not matched (ie user entered bad url), the route that we should redirect to. */
  fallbackRedirect: string;
}

const RoutesComp: FC<Props> = ({ routes, fallbackRedirect }) => {
  return (
    <Suspense fallback={null}>
      <Routes>
        {routes.map(({ load, ...params }, i) => {
          const Component = createLazyComponent(load);
          return (
            <RouteV6
              element={<Component />}
              key={
                params.path
                  ? jsonStringify(params.path)
                  : params.index
                    ? 'index-route'
                    : `route-${i}`
              }
              {...(params as fixMe)}
            />
          );
        })}
        {fallbackRedirect ? (
          <RouteV6
            path="*"
            element={<Navigate to={fallbackRedirect ?? '/'} replace />}
          />
        ) : null}
      </Routes>
    </Suspense>
  );
};

export const LazyRoutes = memo(RoutesComp);
